import { useEffect, useState } from "react";
import { AlertTriangle, IndianRupee, Undo2 } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select, Textarea } from "../../components/ui";
import { payEmployee, adjustPayroll, reversePayment } from "../../api/payroll";
import { fieldErrors, errorMessage, formatMonth } from "../../lib/records";
import { formatMoney } from "../../lib/salary";
import { PAYMENT_MODES, PAYMENT_MODE_LABELS } from "../../lib/enums";


export function PayModal({ open, onClose, row, month, onDone }) {
  const due = row?.dueAmount ?? 0;
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("BANK_TRANSFER");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(due ? String(due) : "");
    setMode("BANK_TRANSFER");
    setReference("");
    setNote("");
    setErrors({});
    setFormError("");
  }, [open, due]);

  const entered = Number(amount || 0);
  const isPartial = entered > 0 && entered < due;

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      await payEmployee(row.employee._id, {
        month,
        ...(amount ? { amount: entered } : {}),
        mode,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a payment"
      description={`${row?.employee?.name ?? "Employee"} · ${formatMonth(month)}`}
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 rounded-md bg-emerald-50/60 p-3 text-center">
          <div>
            <p className="text-xs text-gray-500">Net payable</p>
            <p className="text-sm font-semibold text-gray-800">{formatMoney(row?.netPayable ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Already paid</p>
            <p className="text-sm font-semibold text-gray-800">{formatMoney(row?.paidAmount ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Due</p>
            <p className="text-sm font-bold text-emerald-700">{formatMoney(due)}</p>
          </div>
        </div>

        <Field
          label="Amount"
          error={errors.amount}
          hint={isPartial ? "Less than the balance — the month stays partially paid." : "Leave as-is to settle the month in full."}
        >
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            placeholder={String(due)}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Mode" error={errors.mode}>
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_MODE_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Reference" error={errors.reference} hint="UTR, cheque no. or txn id">
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="UTR9912345"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Note" error={errors.note}>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            <IndianRupee size={15} /> Pay {amount ? formatMoney(entered) : ""}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function AdjustModal({ open, onClose, row, month, onDone }) {
  const [bonus, setBonus] = useState("");
  const [deduction, setDeduction] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const frozen = row?.status === "PAID" || row?.isFrozen;

  useEffect(() => {
    if (!open) return;
    setBonus(row?.bonus ? String(row.bonus) : "");
    setDeduction(row?.deduction ? String(row.deduction) : "");
    setNote(row?.note ?? "");
    setErrors({});
    setFormError("");
  }, [open, row]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      await adjustPayroll(row.employee._id, {
        month,
        bonus: Number(bonus || 0),
        deduction: Number(deduction || 0),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const preview = (row?.earnedAmount ?? 0) + Number(bonus || 0) - Number(deduction || 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bonus & deduction"
      description={`${row?.employee?.name ?? "Employee"} · ${formatMonth(month)}`}
    >
      <form onSubmit={submit} className="space-y-4">
        {frozen && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              This month is fully paid and its figures are frozen. Reverse the payment first to edit it.
            </span>
          </div>
        )}
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Bonus" error={errors.bonus}>
            <Input
              value={bonus}
              onChange={(e) => setBonus(e.target.value.replace(/[^\d.]/g, ""))}
              inputMode="decimal"
              placeholder="0"
              disabled={frozen}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Deduction" error={errors.deduction}>
            <Input
              value={deduction}
              onChange={(e) => setDeduction(e.target.value.replace(/[^\d.]/g, ""))}
              inputMode="decimal"
              placeholder="0"
              disabled={frozen}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Note" error={errors.note}>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            disabled={frozen}
            placeholder="Festival bonus"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex items-center justify-between rounded-md bg-emerald-50/60 px-3.5 py-2.5">
          <span className="text-sm text-gray-600">
            Earned {formatMoney(row?.earnedAmount ?? 0)} → net payable
          </span>
          <span className="text-base font-bold text-emerald-700">{formatMoney(preview)}</span>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            disabled={frozen}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            Save adjustment
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * §11.7 — reverse a payment. Recorded as a negative ledger entry (nothing is
 * deleted) and the month reopens for editing.
 */
export function ReverseModal({ open, onClose, row, month, onDone }) {
  const paid = row?.paidAmount ?? 0;
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(paid ? String(paid) : "");
    setNote("");
    setFormError("");
  }, [open, paid]);

  async function submit(e) {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await reversePayment(row.employee._id, {
        month,
        ...(amount ? { amount: Number(amount) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reverse a payment"
      description={`${row?.employee?.name ?? "Employee"} · ${formatMonth(month)}`}
      maxWidth="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <p className="rounded-md bg-amber-50 border border-amber-200 px-3.5 py-3 text-sm text-amber-800">
          For a wrong amount, a double payment, or the wrong person. Nothing is deleted — the reversal
          is added to the ledger as a negative entry and the month reopens for editing.
        </p>

        <Field label="Amount to reverse" hint={`${formatMoney(paid)} has been paid this month.`}>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            placeholder={String(paid)}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <Field label="Reason">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Paid twice by mistake"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button type="submit" variant="destructive" loading={saving} className="w-full sm:w-auto">
            <Undo2 size={15} /> Reverse
          </Button>
        </div>
      </form>
    </Modal>
  );
}
